import React, { useState, useEffect, useRef } from "react";
import { Document, Page } from "react-pdf";
import "./pdfWorker";
import { ReactComponent as RightArrow} from "../../../static/svgs/chevron-circle-left.svg";
import {ReactComponent as LeftArrow} from "../../../static/svgs/chevron-circle-right.svg";
import {ReactComponent as ErrorFileIcon} from "../../../static/svgs/file-circle-exclamation.svg";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

// Detect Google Drive link and return embeddable preview URL
// const getGoogleDriveEmbedUrl = (url) => {
//   if (!url) return null;
//   const match = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
//   if (match) {
//     return `https://drive.google.com/file/d/${match[1]}/preview`;
//   }
//   return null;
// };

export default function PdfViewerMUI({ file }) {
  // const googleEmbedUrl = getGoogleDriveEmbedUrl(file);
  const [numPages, setNumPages] = useState(null);
  const [page, setPage] = useState(1);
  const [hasError, setHasError] = useState(false);

  // Reset error state when file changes
  useEffect(() => {
    setHasError(false);
  }, [file]);

  const containerRef = useRef(null);
  const [pdfWidth, setPdfWidth] = useState(500);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        // Subtract 32px for padding (16px each side), min 250px, max 600px
        setPdfWidth(Math.max(250, Math.min(width - 28, 600)));
      }
    };
    updateWidth();

    // ResizeObserver catches zoom, container, and window resizes
    let resizeObserver;
    if (containerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateWidth);
      resizeObserver.observe(containerRef.current);
    }
    // Also listen to window resize as a fallback
    window.addEventListener('resize', updateWidth);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const scaleFactor = window.devicePixelRatio || 1.5;

  const nextPage = () => setPage((p) => Math.min(numPages || 1, p + 1));
  const prevPage = () => setPage((p) => Math.max(1, p - 1));

  return (
    <div className="flex justify-center py-10">
        <div className="relative w-full" ref={containerRef}>
        {/* Google Drive files: use iframe embed
        {googleEmbedUrl ? (
          <div className="border-[1.1px] border-[#A7ADB1] rounded bg-white max-w-full p-4 overflow-hidden">
            <iframe
              src={googleEmbedUrl}
              width={pdfWidth}
              height={pdfWidth * 1.4}
              style={{ border: "none" }}
              title="PDF Viewer"
              allow="autoplay"
            />
          </div>
        ) : (
        <> */}
        {!hasError && (
          <div className="absolute -top-8 right-0 flex gap-3 z-10">
            <LeftArrow
              className={page <= 1 ? "ml-2 opacity-40 cursor-not-allowed" : "cursor-pointer ml-2"}
              onClick={page <= 1 ? undefined : prevPage}
            />
            <RightArrow
              className={page >= numPages ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}
              onClick={page >= numPages ? undefined : nextPage}
            />
          </div>
        )}
        {hasError ? (
          <div className="flex flex-col items-center justify-center w-full h-[400px]">
            <ErrorFileIcon/>
            <div className="font-light text-2xl">Error Loading File</div>
            <div className="font-normal text-base mt-[13px] w-[446px] text-center">There was an error in uploading the student's artifact.</div>
              <a href={file} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline mt-2">Open file in new tab</a>
          </div>
        ) : (
          <div className="border-[1.1px] border-[#A7ADB1] rounded bg-white max-w-full p-4 overflow-hidden">
            <Document
              file={file}
              onLoadSuccess={({ numPages }) => { setNumPages(numPages); setHasError(false); }}
              onLoadError={(error) => { 
                // Ignore "Worker was destroyed" — this fires during route transitions, not real errors
                if (error?.message?.includes("Worker was destroyed")) return;
                console.error("PdfViewerMUI onLoadError fired!");
                console.error("Error message:", error?.message);
                console.error("File URL:", file);
                setHasError(true); 
              }}
              loading={<div className="text-center text-gray-500">Loading PDF…</div>}
              error={null}
            >
              <Page
                pageNumber={page}
                className="pdfPage"
                renderTextLayer={false}
                renderAnnotationLayer={false}
                width={pdfWidth}
                scale={1}
                renderMode="canvas"
              />
            </Document>
            {numPages && (
              <div className="mt-2 text-center text-sm text-gray-600">
                Page {page} of {numPages}
              </div>
            )}
          </div>
        )}
        {/* </>
        )} */}
      </div>
    </div>
  );
}